/**
 * PredaniModal — "Předat vůz": the handover of one reservation (P4-d). One surface for what the paper protocol had:
 * the drivers (picked from the customer's own řidiči, documents shown as they are), the km, the checklist, the
 * deposit as a fact, a note. A customer rental's drivers are saved with their own button FIRST — the server's pre-flight
 * (`chybi`, what still blocks the handover) is computed from the saved set, so the dialog shows the server's word, never
 * its own. A `pujcovna:admin` may hand over although something is missing, with a reason that is logged and printed.
 * Same rules as the other rental modals: the form renders only from reads that finished after it opened, its controls
 * freeze while a request is in flight, and it cannot be dismissed while one is pending.
 */

import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { Radio } from '@/components/Radio';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt } from '@/lib/format';
import { pragueToday } from '@/lib/prague-date';
import {
  DOKLAD_STATUS_LABEL,
  DOKLAD_STATUS_TONE,
  DOKLAD_TYP_LABEL,
  dokladStatus,
  ridicFullName,
} from '@/modules/zakaznici/doklad-helpers';
import { useRidiciQuery, type Ridic } from '@/modules/zakaznici/driver-api';
import {
  useFleetQuery,
  useKontrolyTerminyQuery,
  usePredaniPripravaQuery,
  useZavadyQuery,
  usePredatMutation,
  useRezervaceRidiciQuery,
  useSaveRezervaceRidiciMutation,
  type PredaniPriprava,
  type Rezervace,
} from './api';
import { upozorneniNaPredani } from './predani-upozorneni';
import {
  CISTOTA,
  KAUCE_ZPUSOB,
  KONTROLNI_BODY,
  PALIVO,
  PRAZDNY_FORMULAR,
  ZAPACH,
  digits,
  jeZmenen,
  kauceDleRezervace,
  kauceKPredvyplneni,
  nastavHlavniho,
  predaniBody,
  predaniZablokovano,
  prepniRidice,
  ridiciBody,
  vyberZeRezervace,
  type Cistota,
  type KauceZpusob,
  type Palivo,
  type PredaniForm,
  type VyberRidicu,
  type Zapach,
} from './predani-helpers';
import { formatTermin } from './rezervace-helpers';
import { PredaniFotky } from './PredaniFotky';

const MAX_RIDICU = 5;

const FAZE_TEXT: Record<Exclude<PredaniPriprava['faze'], 'predani'>, string> = {
  vraceni: 'Vozidlo je už předáno — zbývá ho přijmout zpět.',
  hotovo: 'Vozidlo je vráceno — zápůjčka je ukončená.',
  nelze: 'Rezervace je zrušená — vozidlo nelze předat.',
};

interface PredaniModalProps {
  rezervace: Rezervace;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  /** `pujcovna:admin`: may hand over although required data is missing, with a reason. */
  canOverride: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The documents of one driver, factual: type, number, validity — the verdict on them is the server's `chybi`. */
function DokladyRidice({ ridic }: { ridic: Ridic }) {
  if (ridic.doklady.length === 0) {
    return <span className="text-[12px] text-ink-3">Bez dokladů</span>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {ridic.doklady.map((d) => {
        const st = dokladStatus(d.datum_platnosti);
        return (
          <li
            key={d.id_dokladu}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-2"
          >
            <span>{DOKLAD_TYP_LABEL[d.typ]}</span>
            <span className="font-mono text-ink">{d.cislo || 'bez čísla'}</span>
            <span className="text-ink-3">
              {d.datum_platnosti ? `platí do ${formatDate(d.datum_platnosti)}` : 'bez platnosti'}
            </span>
            {st !== 'platny' && (
              <Badge tone={DOKLAD_STATUS_TONE[st]} size="sm">
                {DOKLAD_STATUS_LABEL[st]}
              </Badge>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The car's state worth a look before it goes out: STK / servis dates, open checks, recorded faults. Advice only — it never blocks the
 * handover and shows nothing while the reads are pending or if one fails (the fleet entry, the fleet-wide checks feed and the damage map
 * are all plain reads of the rental module).
 */
function PredaniUpozorneni({ rezervace }: { rezervace: Rezervace }) {
  const fleet = useFleetQuery();
  const kontroly = useKontrolyTerminyQuery();
  const zavady = useZavadyQuery(rezervace.id_vozidla);
  const car = fleet.data?.find((f) => f.id === rezervace.id_vozidla);
  if (!car) return null;
  const upozorneni = upozorneniNaPredani({
    dnes: pragueToday(),
    konec: rezervace.do,
    stk: car.vozidlo?.stk_platnost,
    serviska: car.vozidlo?.serviska,
    kontroly: (kontroly.data?.terminy ?? []).filter((k) => k.id_vozidla === rezervace.id_vozidla),
    zavady: zavady.data ?? [],
  });
  if (upozorneni.length === 0) return null;
  return (
    <section data-testid="predani-upozorneni" role="status" className="flex flex-col gap-2 border border-warn bg-warn-soft p-3">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-warn">Před předáním zkontrolujte</div>
      <ul className="flex flex-col gap-1 text-[12.5px]">
        {upozorneni.map((u) => (
          <li key={u.id} data-testid={`predani-upozorneni-${u.id}`} className={u.ton === 'red' ? 'text-red-text' : u.ton === 'warn' ? 'text-warn' : 'text-ink-2'}>
            {u.text}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PredaniFormular({
  rezervace,
  title,
  canOverride,
  priprava,
  ulozeno,
  onOpenChange,
  onBusyChange,
}: {
  rezervace: Rezervace;
  title: string;
  canOverride: boolean;
  priprava: PredaniPriprava;
  ulozeno: VyberRidicu;
  onOpenChange: (open: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const technicka = priprava.typ === 'technicka';
  const ridiciQ = useRidiciQuery(technicka ? null : rezervace.id_zakaznika, { fresh: true });
  const saveRidici = useSaveRezervaceRidiciMutation(rezervace.id);
  const predat = usePredatMutation(rezervace.id, rezervace.id_vozidla);
  const [form, setForm] = useState<PredaniForm>(() => ({
    ...PRAZDNY_FORMULAR,
    km: priprava.km_posledni != null ? String(priprava.km_posledni) : '',
  }));
  const [vyber, setVyber] = useState<VyberRidicu>(ulozeno);
  // a photo in flight counts too: the dialog neither closes nor submits under it (the protocol freezes the photo set)
  const [fotkyBusy, setFotkyBusy] = useState(false);
  const formBusy = saveRidici.isPending || predat.isPending;
  const busy = formBusy || fotkyBusy;
  const set = <K extends keyof PredaniForm>(k: K, v: PredaniForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // lift the busy state so the shell can refuse to dismiss while a request is in flight
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);

  const zmenenyRidici = !technicka && jeZmenen(vyber, ulozeno);
  const potrebujePrekrocit = !technicka && priprava.chybi.length > 0;
  const zablokovano = predaniZablokovano({
    km: form.km,
    zmenenyRidici,
    technicka,
    chybi: priprava.chybi,
    canOverride,
    overrideDuvod: form.overrideDuvod,
  });

  const ulozitRidice = () => {
    if (busy) return;
    saveRidici.mutate(ridiciBody(vyber), {
      onSuccess: () => toast.ok('Řidiči uloženi.'),
      onError: (err) =>
        toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit řidiče.'}`),
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || zablokovano) return;
    predat.mutate(predaniBody(form, { technicka, override: potrebujePrekrocit && canOverride }), {
      onSuccess: () => {
        toast.ok('Vozidlo předáno.');
        onOpenChange(false);
      },
      onError: (err) => {
        const e2 = err as ApiError;
        // The reservation, the saved drivers and the pre-flight are re-read (the hook invalidates them); what stays in
        // force is named, because the drivers were saved by their own call before this one.
        toast.error(`Vozidlo nebylo předáno: ${e2.message ?? 'Nepodařilo se zapsat předání.'}`);
      },
    });
  };

  const ridici = ridiciQ.data ?? [];

  return (
    <>
      <Modal.Body className="px-8 py-5">
        <form
          id="predani-form"
          data-testid="predani-form"
          onSubmit={submit}
          className="flex flex-col gap-6"
        >
          <p className="text-[13px] text-ink-2">
            <span className="font-mono text-ink">{title}</span> ·{' '}
            {formatTermin(rezervace.od, rezervace.do)}
            {!technicka && rezervace.zakaznik_nazev && <> · {rezervace.zakaznik_nazev}</>}
          </p>

          <PredaniUpozorneni rezervace={rezervace} />

          {!technicka && (
            <section
              data-testid="predani-ridici"
              className="flex flex-col gap-3 border border-line p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Řidiči
                </div>
                <a
                  href={`/zakaznici/${rezervace.id_zakaznika}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[12px] text-accent-text underline"
                >
                  Řidiče a doklady spravujete v detailu zákazníka
                </a>
              </div>
              {ridiciQ.isError ? (
                <p className="text-[13px] text-ink-2">Řidiče zákazníka se nepodařilo načíst.</p>
              ) : !ridiciQ.isFetchedAfterMount || !ridiciQ.data ? (
                <Skeleton className="h-16 w-full" />
              ) : ridici.length === 0 ? (
                <p data-testid="predani-ridici-prazdno" className="text-[13px] text-ink-2">
                  Zákazník zatím nemá žádného řidiče. Přidejte ho v detailu zákazníka a otevřete
                  předání znovu.
                </p>
              ) : (
                <ul className="border border-line">
                  {ridici.map((r) => {
                    const vybran = vyber.ids.includes(r.id_ridice);
                    return (
                      <li
                        key={r.id_ridice}
                        data-testid={`predani-ridic-${r.id_ridice}`}
                        className="flex flex-col gap-2 border-b border-line px-3 py-2.5 last:border-b-0"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <Checkbox
                            label={<span className="text-[14px] text-ink">{ridicFullName(r)}</span>}
                            checked={vybran}
                            disabled={busy || (!vybran && vyber.ids.length >= MAX_RIDICU)}
                            onChange={() => setVyber((v) => prepniRidice(v, r.id_ridice))}
                          />
                          {vybran && (
                            <Radio
                              name="predani-hlavni"
                              label="Hlavní řidič"
                              checked={vyber.hlavni === r.id_ridice}
                              disabled={busy}
                              onChange={() => setVyber((v) => nastavHlavniho(v, r.id_ridice))}
                            />
                          )}
                        </div>
                        <DokladyRidice ridic={r} />
                      </li>
                    );
                  })}
                </ul>
              )}
              {zmenenyRidici && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={ulozitRidice}
                  >
                    Uložit řidiče
                  </Button>
                  <span className="text-[12px] text-ink-3">
                    Výběr řidičů je nutné uložit, teprve pak se přepočítá, co chybí.
                  </span>
                </div>
              )}
              {!zmenenyRidici && priprava.chybi.length > 0 && (
                <div
                  data-testid="predani-chybi"
                  role="alert"
                  className="flex flex-col gap-2 border border-warn bg-warn-soft p-3"
                >
                  <div className="text-[13px] font-medium text-ink">K předání zatím chybí:</div>
                  <ul className="list-disc pl-5 text-[13px] text-ink">
                    {priprava.chybi.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {potrebujePrekrocit && !zmenenyRidici && canOverride && (
            <Fields cols={1}>
              <Field
                label="Důvod předání navzdory chybějícím údajům"
                required
                hint="Zapíše se do historie a vytiskne na předávacím protokolu."
              >
                <TextInput
                  value={form.overrideDuvod}
                  maxLength={200}
                  disabled={busy}
                  data-testid="predani-override"
                  onChange={(e) => set('overrideDuvod', e.currentTarget.value)}
                />
              </Field>
            </Fields>
          )}

          <Fields cols={2}>
            <Field
              label="Nájezd při předání"
              required
              hint={
                priprava.km_posledni != null
                  ? `Poslední známý nájezd: ${formatInt(priprava.km_posledni)} km.`
                  : undefined
              }
            >
              <TextInput
                value={form.km}
                inputMode="numeric"
                suffix="km"
                maxLength={7}
                disabled={busy}
                data-testid="predani-km"
                onChange={(e) => set('km', digits(e.currentTarget.value))}
              />
            </Field>
            <Field label="Poznámka" optional="volitelné">
              <TextInput
                value={form.poznamka}
                maxLength={500}
                disabled={busy}
                data-testid="predani-poznamka"
                onChange={(e) => set('poznamka', e.currentTarget.value)}
              />
            </Field>
          </Fields>

          {!technicka && (
            <>
              <section
                data-testid="predani-seznam"
                className="flex flex-col gap-3 border border-line p-4"
              >
                <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Kontrolní seznam
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {KONTROLNI_BODY.map((b) => (
                    <Checkbox
                      key={b.key}
                      label={b.label}
                      checked={form.body[b.key]}
                      disabled={busy}
                      onChange={(e) =>
                        set('body', { ...form.body, [b.key]: e.currentTarget.checked })
                      }
                    />
                  ))}
                </div>
                <Fields cols={2}>
                  <Field label="Palivo" optional="volitelné">
                    <SelectInput
                      value={form.palivo}
                      disabled={busy}
                      data-testid="predani-palivo"
                      onChange={(e) => set('palivo', e.currentTarget.value as Palivo | '')}
                    >
                      <option value="">— nevyplněno —</option>
                      {PALIVO.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                  <Field label="Čistota" optional="volitelné">
                    <SelectInput
                      value={form.cistota}
                      disabled={busy}
                      data-testid="predani-cistota"
                      onChange={(e) => set('cistota', e.currentTarget.value as Cistota | '')}
                    >
                      <option value="">— nevyplněno —</option>
                      {CISTOTA.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                  <Field label="Zápach" optional="volitelné">
                    <SelectInput
                      value={form.zapach}
                      disabled={busy}
                      data-testid="predani-zapach"
                      onChange={(e) => set('zapach', e.currentTarget.value as Zapach | '')}
                    >
                      <option value="">— nevyplněno —</option>
                      {ZAPACH.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                </Fields>
              </section>

              <section
                data-testid="predani-kauce"
                className="flex flex-col gap-3 border border-line p-4"
              >
                <Checkbox
                  label="Kauce složena"
                  checked={form.kauce.ano}
                  disabled={busy}
                  onChange={(e) => {
                    const ano = e.currentTarget.checked;
                    // ticking offers the deposit the reservation was booked with as the amount (still editable)
                    set('kauce', { ...form.kauce, ano, castka: ano ? kauceKPredvyplneni(rezervace, form.kauce.castka) : form.kauce.castka });
                  }}
                />
                {rezervace.cena != null && (
                  <p data-testid="predani-kauce-dohodnuta" className="text-[12px] text-ink-2">
                    {kauceDleRezervace(rezervace) != null ? (
                      <>
                        Podle rezervace: <span className="font-mono">{formatInt(kauceDleRezervace(rezervace))} Kč</span>
                      </>
                    ) : (
                      'Rezervace je bez kauce.'
                    )}
                  </p>
                )}
                {form.kauce.ano && (
                  <Fields cols={2}>
                    <Field label="Výše kauce" optional="volitelné">
                      <TextInput
                        value={form.kauce.castka}
                        inputMode="numeric"
                        suffix="Kč"
                        maxLength={6}
                        disabled={busy}
                        data-testid="predani-kauce-castka"
                        onChange={(e) =>
                          set('kauce', { ...form.kauce, castka: digits(e.currentTarget.value) })
                        }
                      />
                    </Field>
                    <Field label="Způsob" optional="volitelné">
                      <SelectInput
                        value={form.kauce.zpusob}
                        disabled={busy}
                        data-testid="predani-kauce-zpusob"
                        onChange={(e) =>
                          set('kauce', {
                            ...form.kauce,
                            zpusob: e.currentTarget.value as KauceZpusob | '',
                          })
                        }
                      >
                        <option value="">— nevyplněno —</option>
                        {KAUCE_ZPUSOB.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                  </Fields>
                )}
                <p className="text-[12px] text-ink-3">
                  Zapisuje se jen fakt o kauci, žádná platba se tu neprovádí.
                </p>
              </section>
            </>
          )}

          <PredaniFotky
            rezervaceId={rezervace.id}
            typ="vydani"
            disabled={formBusy}
            onBusyChange={setFotkyBusy}
          />

          {zablokovano && (
            <p data-testid="predani-duvod" className="text-[12px] text-ink-3">
              {zablokovano}
            </p>
          )}
        </form>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => !busy && onOpenChange(false)}
              disabled={busy}
            >
              Zrušit
            </Button>
            <Button
              variant="ink"
              type="submit"
              form="predani-form"
              disabled={busy || zablokovano != null}
            >
              {predat.isPending ? 'Předávám…' : 'Předat vůz'}
            </Button>
          </>
        }
      />
    </>
  );
}

function Info({
  testid,
  onClose,
  children,
  extra,
}: {
  testid: string;
  onClose: () => void;
  children: ReactNode;
  /** Read-only context under the message (the frozen photos of a protocol that is already written). */
  extra?: ReactNode;
}) {
  return (
    <>
      <Modal.Body className="px-8 py-5">
        <p data-testid={testid} className="text-[13px] text-ink-2">
          {children}
        </p>
        {extra && <div className="mt-5 flex flex-col gap-4">{extra}</div>}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={onClose}>
            Zavřít
          </Button>
        }
      />
    </>
  );
}

export function PredaniModal({
  rezervace,
  title,
  canOverride,
  open,
  onOpenChange,
}: PredaniModalProps) {
  const priprava = usePredaniPripravaQuery(rezervace.id);
  const ridici = useRezervaceRidiciQuery(rezervace.id);
  const [busy, setBusy] = useState(false);
  // The form is built once from the pre-flight and the stored drivers, both read AFTER the dialog opened; a later
  // refetch (saving drivers, a refused handover) refreshes `priprava` in place without rebuilding what was typed.
  const fresh =
    priprava.isSuccess &&
    priprava.isFetchedAfterMount &&
    ridici.isSuccess &&
    ridici.isFetchedAfterMount;
  const faze = priprava.data?.faze;

  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={760}>
      <DialogCrown
        eyebrow="Půjčovna"
        title={
          <>
            Předat <em>vůz</em>
          </>
        }
        sub="Předávací protokol: řidiči, nájezd a stav vozu. Po předání se rezervace už nezruší, ukončí ji až přijetí vozidla."
      />
      {priprava.isError || ridici.isError ? (
        <Info testid="predani-error" onClose={() => guarded(false)}>
          Údaje k předání se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
        </Info>
      ) : !fresh || !priprava.data ? (
        <>
          <Modal.Body className="px-8 py-5">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </Modal.Body>
          <Modal.Foot
            actions={
              <Button variant="secondary" onClick={() => guarded(false)}>
                Zrušit
              </Button>
            }
          />
        </>
      ) : faze !== 'predani' ? (
        <Info
          testid="predani-faze"
          onClose={() => guarded(false)}
          // the handover's photos stay reachable once it is written (frozen, read-only)
          extra={
            faze === 'vraceni' || faze === 'hotovo' ? (
              <PredaniFotky rezervaceId={rezervace.id} typ="vydani" nadpis="Fotografie z předání" />
            ) : undefined
          }
        >
          {FAZE_TEXT[faze as Exclude<typeof faze, 'predani' | undefined>]}
        </Info>
      ) : (
        <PredaniFormular
          rezervace={rezervace}
          title={title}
          canOverride={canOverride}
          priprava={priprava.data}
          ulozeno={vyberZeRezervace(ridici.data?.ridici ?? [])}
          onOpenChange={onOpenChange}
          onBusyChange={setBusy}
        />
      )}
    </Modal>
  );
}
